import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ALL_TIME_PRESET } from '@app/storybook/components/datepicker/date-range-presets';
import { Router } from '@angular/router';
import { UserService } from '@app/services/user/user.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ImpactMetricsService } from '@app/services/impact-metrics/impact-metrics.service';
import { MAXX_METADATA_KEY } from '@app/pages/maxx/constants';
import { AbTestingApiService } from '@app/pages/checkout-app/services/api/abtesting.api.service';
import { StrategyCatalogService } from '@app/pages/strategy-catalog/services/strategy-catalog.service';
import {
  StrategyItem,
  getKpiTargetsList,
  optimizeCategoryName,
} from '@app/pages/strategy-catalog/strategy-catalog.models';
import { mapSegmentName } from '@app/pages/checkout-app/checkout-layout/main/components/ab-testing/react/features/list-ab-tests/AbTestCard/utils';
import { StrategyColumns } from '@pdqdev/pdq-shared/dist/entities/StrategyCatalog';
import { Subject } from 'rxjs';
import { takeUntil, switchMap, take, tap, finalize, filter } from 'rxjs/operators';
import {
  stripSegmentIndexPrefix,
  TestWinMetrics,
  OverviewMetrics,
  DirectRevenueMetrics,
  PoweredByPdqMetrics,
  DEFAULT_OVERVIEW_METRICS,
  DEFAULT_DIRECT_REVENUE_METRICS,
  DEFAULT_POWERED_BY_PDQ_METRICS,
  DirectRevenueCard,
  DIRECT_REVENUE_CARDS,
  RevenueUpliftDataPoint,
  WinningTestApiResponse,
  WinningTestsApiResponse,
  DirectRevenueApiResponse,
  RevenueUpliftChartApiResponse,
} from '../impact-dashboard-metric.models';
import { ApiService } from '@app/core/services/api/api.service';
import { TabNames, DASHBOARD_PARAM_DEFAULTS } from '@app/pages/pdq-labs/constants';

@Component({
  selector: 'impact-dashboard-after-abtest-winner',
  standalone: false,
  templateUrl: './impact-dashboard-after-abtest-winner.component.html',
  styleUrl: './impact-dashboard-after-abtest-winner.component.scss',
})
export class ImpactDashboardAfterAbTestWinnerComponent implements OnInit, OnDestroy {
  userName = '';

  overviewMetrics: OverviewMetrics = { ...DEFAULT_OVERVIEW_METRICS };
  directRevenueMetrics: DirectRevenueMetrics = { ...DEFAULT_DIRECT_REVENUE_METRICS };
  poweredByPdqMetrics: PoweredByPdqMetrics = { ...DEFAULT_POWERED_BY_PDQ_METRICS };

  testWins: TestWinMetrics[] = [];
  testWinsTotalCount = 0;
  showAllTestWins = false;
  hasAbTests = false;
  readonly defaultRange = ALL_TIME_PRESET;
  readonly shopCreated = inject(ShopService).shopCreatedSignal;

  private readonly destroy$ = new Subject<void>();

  strategyItemsRecommendedByMaxx: StrategyItem[] = [];
  loadingStrategies = true;

  loadingOverviewMetrics = true;
  loadingDirectRevenueMetrics = true;
  loadingTestWins = true;

  errorOverviewMetrics = false;
  errorDirectRevenueMetrics = false;
  errorTestWins = false;
  errorStrategies = false;

  private currentDirectRevenueStartDate?: Date;
  private currentDirectRevenueEndDate?: Date;
  readonly StrategyColumns = StrategyColumns;
  readonly getKpiTargetsList = getKpiTargetsList;
  readonly optimizeCategoryName = optimizeCategoryName;

  loadingPoweredByPdqMetrics = true;
  errorPoweredByPdqMetrics = false;

  loadingAvgResponseTime = true;
  errorAvgResponseTime = false;

  loadingIntegrationTriggers = true;
  errorIntegrationTriggers = false;

  isMaxxEnabled = false;

  // Hide ROI for shops with billing plan > 365 days
  hideRoi = false;

  revenueUpliftDataPoints: RevenueUpliftDataPoint[] = [];
  loadingRevenueUpliftChart = true;
  errorRevenueUpliftChart = false;
  hasRevenueUpliftData = false;

  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly abTestingApiService: AbTestingApiService,
    private readonly strategyCatalogService: StrategyCatalogService,
    private readonly shopService: ShopService,
    private readonly impactMetricsService: ImpactMetricsService,
    private readonly apiService: ApiService,
  ) {}

  get currencySymbol(): string {
    return this.shopService.shopCurrencySymbol || '$';
  }

  get sortedDirectRevenueCards(): DirectRevenueCard[] {
    return [...DIRECT_REVENUE_CARDS].sort((a, b) => {
      const aHasRevenue = this.directRevenueMetrics[a.hasRevenueKey] as boolean;
      const bHasRevenue = this.directRevenueMetrics[b.hasRevenueKey] as boolean;

      // Cards with revenue come first, then alphabetically
      if (aHasRevenue !== bHasRevenue) {
        return aHasRevenue ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });
  }

  ngOnInit(): void {
    this.loadUserName();
    this.loadTestWins();
    this.loadPoweredByPdqMetrics();
    this.loadAvgResponseTime();
    this.loadIntegrationTriggers();
    this.loadRevenueUpliftChartData();
    this.waitForShopDataAndLoadCurrencyMetrics();
  }

  private waitForShopDataAndLoadCurrencyMetrics(): void {
    this.shopService.shop$
      .pipe(
        filter((shop) => !!shop?.metadata?.currency),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadOverviewMetricsWithBillingCheck();
        this.checkMaxxEnabled();
      });
  }

  private loadRevenueUpliftChartData(): void {
    this.loadingRevenueUpliftChart = true;
    this.errorRevenueUpliftChart = false;

    this.abTestingApiService
      .get('ab-tests/revenue-uplift-chart')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: RevenueUpliftChartApiResponse) => {
          this.revenueUpliftDataPoints = response.dataPoints || [];
          this.hasRevenueUpliftData = this.revenueUpliftDataPoints.length > 0;
          this.loadingRevenueUpliftChart = false;
        },
        error: (err) => {
          console.error('Failed to load revenue uplift chart data', err);
          this.loadingRevenueUpliftChart = false;
          this.errorRevenueUpliftChart = true;
          this.hasRevenueUpliftData = false;
        },
      });
  }

  onRefreshRevenueUpliftChart(): void {
    this.loadRevenueUpliftChartData();
  }

  private checkMaxxEnabled(): void {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.isMaxxEnabled = !!shop?.metadata?.[MAXX_METADATA_KEY];
      if (this.isMaxxEnabled) {
        this.loadRecommendedStrategies();
      }
    });
  }

  private loadOverviewMetricsWithBillingCheck(): void {
    this.loadingOverviewMetrics = true;
    this.errorOverviewMetrics = false;

    this.impactMetricsService
      .loadOverviewMetricsWithBillingCheck(this.currencySymbol)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingOverviewMetrics = false)),
      )
      .subscribe({
        next: ({ hideRoi, metrics }) => {
          this.hideRoi = hideRoi;

          if (metrics) {
            this.overviewMetrics = metrics;
          } else {
            this.errorOverviewMetrics = true;
          }
        },
        error: () => {
          this.errorOverviewMetrics = true;
          this.hideRoi = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserName(): void {
    this.userService.currentPdqUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user?.name) {
        this.userName = user.name.split(' ')[0];
      }
    });
  }

  onDirectRevenueDateChange(event: { start: number; end: number; rangePreset: string }): void {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    this.loadDirectRevenueMetrics(startDate, endDate);
  }

  private readonly configureRoutes: Record<string, string> = {
    configureCheckoutUpsell: '/convert/extensions/upsell/new',
    configureTrackingPage: '/retain/tracking-page',
    configureOrderEditing: '/retain/post-purchase',
    configureCashback: '/convert/extensions/pdq-cash-back/new',
    configureDigitalUpsell: '/convert/extensions/custom-upsell/new',
    configureGiftUnlock: '/convert/extensions/gift-unlock/new',
  };

  onConfigureFeature(action: string): void {
    const route = this.configureRoutes[action];
    if (route) {
      this.router.navigate([route]);
    }
  }

  private loadTestWins(): void {
    this.loadingTestWins = true;
    this.errorTestWins = false;
    this.abTestingApiService
      .get('ab-tests/winning-tests-metric')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: WinningTestsApiResponse) => {
          this.testWinsTotalCount = response.totalCount;
          this.hasAbTests = response.totalCount > 0;
          this.testWins = response.tests.map((test) => this.mapTestToTestWin(test));
          this.loadingTestWins = false;
        },
        error: (err) => {
          console.error('Failed to load test wins', err);
          this.hasAbTests = false;
          this.loadingTestWins = false;
          this.errorTestWins = true;
        },
      });
  }

  onRefreshTestWins(): void {
    this.loadTestWins();
  }

  private mapTestToTestWin(test: WinningTestApiResponse): TestWinMetrics {
    const mappedSegment = mapSegmentName(test.segmentImplemented);
    const fallbackSegmentLabel = mappedSegment !== '-' ? mappedSegment : 'All Users';
    return {
      id: String(test.id),
      name: test.name,
      // the backend label mirrors the analytics segment filter, so the card
      // can never contradict the deep link
      implementedSegment: stripSegmentIndexPrefix(test.segmentImplementedClean) ?? fallbackSegmentLabel,
      segmentImplementedClean: test.segmentImplementedClean ?? null,
      revenuePerCheckout: this.formatUpliftValue(test.upliftArpcAbs),
      revenuePerCheckoutChange: this.formatPercentageChange(test.upliftArpcRel),
      annualizedImpact: this.impactMetricsService.formatCurrency(test.annualizedUpliftArpc, this.currencySymbol),
      startedAt: this.formatDate(test.startedAt),
    };
  }

  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private formatUpliftValue(value: number | null | undefined): string {
    if (value == null) return `+${this.currencySymbol}0.00`;
    const sign = value >= 0 ? '+' : '';
    return `${sign}${Math.abs(value).toFixed(2)}`;
  }

  private formatPercentageChange(value: number | null | undefined): string {
    if (value == null) return '+0%';
    const sign = value >= 0 ? '+' : '';
    const decimals = Math.abs(value) < 1 ? 2 : 1;
    return `${sign}${value.toFixed(decimals)}%`;
  }

  get displayedTestWins(): TestWinMetrics[] {
    return this.showAllTestWins ? this.testWins : this.testWins.slice(0, 3);
  }

  get remainingTestWinsCount(): number {
    return Math.max(0, this.testWins.length - 3);
  }

  onToggleShowAllTestWins(): void {
    this.showAllTestWins = !this.showAllTestWins;
  }

  onViewAllTestWins(): void {
    this.router.navigate(['/optimize/dashboard'], {
      queryParams: {
        testName: DASHBOARD_PARAM_DEFAULTS.NO_FILTER,
        CrossSitetestTestName: DASHBOARD_PARAM_DEFAULTS.NO_FILTER,
        ShowCheckoutORCrossSiteAB: DASHBOARD_PARAM_DEFAULTS.CHECKOUT_AB_TEST,
        sheetName: TabNames.ALL_TESTS,
      },
    });
  }

  onViewTestDetails(testWin: TestWinMetrics): void {
    const queryParams: Record<string, string> = { testName: testWin.name };
    if (testWin.segmentImplementedClean) {
      queryParams.segmentparameter = testWin.segmentImplementedClean;
    }
    this.router.navigate(['/optimize/dashboard'], { queryParams });
  }

  onConfigureAbTests(): void {
    this.router.navigate(['/optimize/ab-tests']);
  }

  private loadRecommendedStrategies(): void {
    this.loadingStrategies = true;
    this.errorStrategies = false;
    this.strategyCatalogService
      .getStrategies()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((strategies) =>
          this.strategyCatalogService.getRecommendedStrategies().pipe(
            take(1),
            tap((recommended) => {
              const recommendedIds = recommended?.ids?.split(',').slice(0, 3) ?? [];
              this.strategyItemsRecommendedByMaxx = strategies.filter((item) =>
                recommendedIds.includes(item[StrategyColumns.ID].toString()),
              );
              this.loadingStrategies = false;
            }),
          ),
        ),
      )
      .subscribe({
        error: (err) => {
          console.error('Failed to load strategies', err);
          this.loadingStrategies = false;
          this.errorStrategies = true;
        },
      });
  }

  onRefreshStrategies(): void {
    this.loadRecommendedStrategies();
  }

  navigateToStrategyDetails(item: StrategyItem): void {
    this.router.navigate(['/optimize/strategy-catalog', item[StrategyColumns.ID]]);
  }

  onRefreshOverviewMetrics(): void {
    this.loadOverviewMetricsWithBillingCheck();
  }

  private loadDirectRevenueMetrics(start: Date, end: Date): void {
    this.currentDirectRevenueStartDate = start;
    this.currentDirectRevenueEndDate = end;

    const startDateStr = this.impactMetricsService.formatDateForApi(start);
    const endDateStr = this.impactMetricsService.formatDateForApi(end);

    this.loadingDirectRevenueMetrics = true;
    this.errorDirectRevenueMetrics = false;
    this.abTestingApiService
      .get(`ab-tests/direct-revenue-metrics?startDate=${startDateStr}&endDate=${endDateStr}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: DirectRevenueApiResponse | null) => {
          if (response) {
            this.directRevenueMetrics = {
              checkoutUpsellRevenue: this.impactMetricsService.formatCurrency(
                response.checkoutUpsellRevenue,
                this.currencySymbol,
              ),
              trackingPageRevenue: this.impactMetricsService.formatCurrency(
                response.trackingPageRevenue,
                this.currencySymbol,
              ),
              orderEditingRevenue: this.impactMetricsService.formatCurrency(
                response.orderEditingRevenue,
                this.currencySymbol,
              ),
              cashbackRewards: this.impactMetricsService.formatCurrency(response.cashbackRevenue, this.currencySymbol),
              digitalUpsellRevenue: this.impactMetricsService.formatCurrency(
                response.digitalUpsellRevenue,
                this.currencySymbol,
              ),
              giftUnlockRevenue: this.impactMetricsService.formatCurrency(
                response.giftUnlockRevenue,
                this.currencySymbol,
              ),
              hasCheckoutUpsellRevenue: response.hasEverCheckoutUpsellRevenue ?? false,
              hasTrackingPageRevenue: response.hasEverTrackingPageRevenue ?? false,
              hasOrderEditingRevenue: response.hasEverOrderEditingRevenue ?? false,
              hasCashbackRevenue: response.hasEverCashbackRevenue ?? false,
              hasDigitalUpsellRevenue: response.hasEverDigitalUpsellRevenue ?? false,
              hasGiftUnlockRevenue: response.hasEverGiftUnlockRevenue ?? false,
            };
          }
          this.loadingDirectRevenueMetrics = false;
        },
        error: (err) => {
          console.error('Failed to load direct revenue metrics', err);
          this.loadingDirectRevenueMetrics = false;
          this.errorDirectRevenueMetrics = true;
        },
      });
  }

  onRefreshDirectRevenueMetrics(): void {
    this.loadDirectRevenueMetrics(this.currentDirectRevenueStartDate, this.currentDirectRevenueEndDate);
  }

  private loadPoweredByPdqMetrics(): void {
    this.loadingPoweredByPdqMetrics = true;
    this.errorPoweredByPdqMetrics = false;

    this.abTestingApiService
      .get('ab-tests/powered-by-pdq-metrics')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.poweredByPdqMetrics = {
            ...this.poweredByPdqMetrics,
            checkoutsPowered: this.formatNumber(response?.checkoutsPowered),
            trackingPageVisits: this.formatNumber(response?.trackingPageVisits),
          };
          this.loadingPoweredByPdqMetrics = false;
        },
        error: (err) => {
          console.error('Failed to load powered-by-pdq metrics', err);
          this.loadingPoweredByPdqMetrics = false;
          this.errorPoweredByPdqMetrics = true;
        },
      });
  }

  private loadAvgResponseTime(): void {
    this.loadingAvgResponseTime = true;
    this.errorAvgResponseTime = false;

    this.apiService
      .get('coralogix/avg-response-time-metrics?days_back=7')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.poweredByPdqMetrics = {
            ...this.poweredByPdqMetrics,
            avgResponseTime: this.formatResponseTime(response?.avgResponseTime),
          };
          this.loadingAvgResponseTime = false;
        },
        error: (err) => {
          console.error('Failed to load avg response time', err);
          this.loadingAvgResponseTime = false;
          this.errorAvgResponseTime = true;
        },
      });
  }

  private formatResponseTime(value: number | null | undefined): string {
    if (value == null) return '--';
    return `${value}ms`;
  }

  private loadIntegrationTriggers(): void {
    this.loadingIntegrationTriggers = true;
    this.errorIntegrationTriggers = false;

    this.apiService
      .get('coralogix/integration-triggers-metrics?days_back=7')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.poweredByPdqMetrics = {
            ...this.poweredByPdqMetrics,
            integrationTriggers: this.formatNumber(response?.totalTriggers),
            connectedIntegrations: response?.connectedIntegrations || [],
          };
          this.loadingIntegrationTriggers = false;
        },
        error: (err) => {
          console.error('Failed to load integration triggers', err);
          this.loadingIntegrationTriggers = false;
          this.errorIntegrationTriggers = true;
        },
      });
  }

  onRefreshPoweredByPdqMetrics(): void {
    this.loadPoweredByPdqMetrics();
  }

  onRefreshAvgResponseTime(): void {
    this.loadAvgResponseTime();
  }

  onRefreshIntegrationTriggers(): void {
    this.loadIntegrationTriggers();
  }

  onNavigateToIntegrations(): void {
    this.router.navigate(['/settings/integrations']);
  }

  private formatNumber(value: number | null | undefined): string {
    if (value == null || value === 0) return '0';
    return Math.round(value).toLocaleString('en-US');
  }
}
